import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import AmountField from "../../../common/components/functional/AmountField";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import {
  FlexVerticalAligned,
  RoundedButton,
} from "../../../common/components/styled/styled";
import { AdditionalInfoFormContainer } from "../additionalInfo/styles";
import { OffersStatus } from "../orderAPI";
import { AutomatchingStatus } from "../orderTypes";
import { StatusOverviewBlock } from "./StatusOverviewBlock";
import theme from "../../../theme";
import { useAppSelector } from "../../../app/hooks";
import { selectOrderRowById } from "../orderRowsDetailedSlice";
import { automatchingIsNotInThePast } from "../utils";
import InputHelperText from "../../../common/components/functional/error/InputHelperText";
import { AutomatchingDelayWarning } from "../AutomatchingDelayWarning";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import { useState } from "react";

type Props = {
  offers: OffersStatus;
  disableManualOffers: boolean;
  bundleId: string;
  openSearchDialog: () => void;
  handleAutomatchingStatusChange: (automatchingStatus: boolean) => void;
  handleAutomatchingDelayChange: (minutes: number) => void;
  automatchingInitialStatus: AutomatchingStatus;
  automatchingStartDelayMinutes: number | null;
  disableAutomatchingToggle: boolean;
  automatchingDelayWarning?: string;
};

export const OffersStatusBlock = ({
  offers,
  disableManualOffers,
  bundleId,
  openSearchDialog,
  handleAutomatchingStatusChange,
  handleAutomatchingDelayChange,
  automatchingInitialStatus,
  automatchingStartDelayMinutes,
  disableAutomatchingToggle,
  automatchingDelayWarning,
}: Props) => {
  const { t } = useTranslation("order");

  const bundle = useAppSelector((state) => selectOrderRowById(state, bundleId));
  const start = new Date(bundle.orderRows[0].shiftDetail.time.start);
  const isAutomatchingExhausted = bundle.status?.isAutomatchingExhausted;

  const [startAutomatching, setStartAutomatching] = useState(false);

  const isInitialAutomatchingOn =
    automatchingInitialStatus === AutomatchingStatus.On;
  const isInitialAutomatchingPaused =
    automatchingInitialStatus === AutomatchingStatus.Paused;
  const triesToAutomatchInThePast =
    startAutomatching && !automatchingIsNotInThePast(start);

  const canEditDelay =
    !isInitialAutomatchingOn &&
    !isAutomatchingExhausted &&
    !triesToAutomatchInThePast;

  const getAutomatchingStatusLabel = () => {
    return (
      <FlexVerticalAligned>
        {isInitialAutomatchingOn
          ? t("automatchingIsOn")
          : t("automatchingIsOff")}
        {isInitialAutomatchingPaused && (
          <Chip
            variant="outlined"
            color="warning"
            sx={{ fontWeight: 600, ml: 1, mb: 0 }}
            label={t("automatchingPaused")}
          />
        )}
        {isAutomatchingExhausted && (
          <Chip
            variant="outlined"
            color="warning"
            sx={{ fontWeight: 600, ml: 1, mb: 0 }}
            label={t("automatchingExhausted")}
          />
        )}
      </FlexVerticalAligned>
    );
  };

  return (
    <AdditionalInfoFormContainer>
      <FormSection title={t("automatchingOffers")}>
        <Grid container item my={0.5}>
          <Typography>{getAutomatchingStatusLabel()}</Typography>
        </Grid>
        <Grid container item mb={0.5}>
          {automatchingStartDelayMinutes ? (
            <Box>
              <FlexVerticalAligned mb={0.5}>
                <Typography variant="secondary">
                  {t("automatchingDelayMinutes")}
                </Typography>
                <InfoTooltip
                  title={t("automatchingDelayInfo")}
                  style={{ marginLeft: 2 }}
                />
              </FlexVerticalAligned>
              <AmountField
                sx={{ width: "140px" }}
                size="small"
                disabled={!canEditDelay}
                value={automatchingStartDelayMinutes || 30}
                onChange={(value: number) =>
                  handleAutomatchingDelayChange(Math.max(1, value))
                }
                min={1}
                max={99999}
              />
              {automatchingDelayWarning && <AutomatchingDelayWarning />}
            </Box>
          ) : (
            <Typography variant="secondary">
              {t("automatchingDelayNotSet")}
            </Typography>
          )}
        </Grid>

        {!isInitialAutomatchingOn && !isInitialAutomatchingPaused && (
          <Grid container item>
            <FormControlLabel
              disabled={disableAutomatchingToggle}
              onChange={(_, checked) => {
                handleAutomatchingStatusChange(checked);
                setStartAutomatching(checked);
              }}
              checked={startAutomatching}
              control={<Switch />}
              label={t("startAutomatching")}
            />
            <InputHelperText
              errorMessage={
                triesToAutomatchInThePast ? t("cannotAutomatchInPast") : ""
              }
              helperText={
                isAutomatchingExhausted && startAutomatching
                  ? t("restartExhaustedAutomatchingInfo")
                  : ""
              }
              TypographyProps={{
                style: { marginTop: "-5px" },
              }}
            />
          </Grid>
        )}

        <Grid container item sx={{ marginTop: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>{t("currentOffers")}</Typography>
          <StatusOverviewBlock offers={offers.automatchOffers} />
          {isAutomatchingExhausted ? (
            <Typography
              fontSize={16}
              color={theme.palette.warning.main}
              sx={{
                backgroundColor: "#FAFAFB",
                borderRadius: "4px",
                marginTop: "8px",
                padding: "8px",
              }}
            >
              {t("automatchingIsExhausted")}
            </Typography>
          ) : null}
        </Grid>
      </FormSection>
      {/* disable manual offer button if order row is in automatching */}
      <FormSection title={t("manualOffers")} noBorder>
        <Grid item xs={12}>
          <Box hidden={offers.manualOffers.workOffersTotal === 0}>
            <StatusOverviewBlock offers={offers.manualOffers} />
          </Box>
        </Grid>
        <Grid item>
          <RoundedButton
            disabled={disableManualOffers}
            variant="outlined"
            onClick={openSearchDialog}
            sx={{ marginTop: 2 }}
          >
            <SearchIcon />
            {t("searchTalentsManually")}
          </RoundedButton>
        </Grid>
      </FormSection>
    </AdditionalInfoFormContainer>
  );
};
